import { Box, Card, CardContent, CardMedia, Typography, Chip, Button, Avatar, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDate, formatDateEN, formatReadTime, formatCategory } from '../utils/formatters';
import type { BlogPost } from '../types/blog';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '24px',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 41, 59, 0.8)'
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-12px)',
    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(37, 99, 235, 0.3)'}`,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 24px 60px rgba(16, 185, 129, 0.25), 0 0 40px rgba(37, 99, 235, 0.15)'
      : '0 24px 60px rgba(37, 99, 235, 0.2), 0 0 40px rgba(16, 185, 129, 0.1)',
    '& .blog-image': {
      transform: 'scale(1.1)',
    },
    '& .read-more-button': {
      transform: 'translateX(8px)',
    },
  },
}));

const StyledCardMedia = styled(CardMedia)({
  height: 280,
  position: 'relative',
  overflow: 'hidden',
  '& img': {
    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  },
});

const CategoryChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  background: theme.palette.mode === 'dark'
    ? 'rgba(16, 185, 129, 0.2)'
    : 'rgba(37, 99, 235, 0.15)',
  backdropFilter: 'blur(12px)',
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(37, 99, 235, 0.3)'}`,
  color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.primary.main,
  fontWeight: 700,
  fontSize: '0.85rem',
  zIndex: 2,
}));

const ReadMoreButton = styled(Button)(({ theme }) => ({
  marginTop: 'auto',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#FFFFFF',
  padding: '10px 24px',
  borderRadius: '12px',
  fontWeight: 700,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(16, 185, 129, 0.3)'
      : '0 8px 24px rgba(37, 99, 235, 0.25)',
  },
}));

interface BlogPostCardProps {
  post: BlogPost;
}

export default function BlogPostCard({ post }: BlogPostCardProps) {
  const { language } = useLanguage();
  
  const title = language === 'tr' ? post.titleTR : post.titleEN;
  const excerpt = language === 'tr' ? post.excerptTR : post.excerptEN;
  const formattedDate = language === 'tr' ? formatDate(post.publishDate) : formatDateEN(post.publishDate);
  const readTime = formatReadTime(post.readTime, language);
  const category = formatCategory(post.category, language);

  return (
    <StyledCard>
      <Box sx={{ position: 'relative' }}>
        <StyledCardMedia
          component="img"
          image={post.featuredImage}
          alt={title}
          className="blog-image"
        />
        <CategoryChip label={category} size="small" />
      </Box>
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: '1.25rem', md: '1.5rem' },
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 3,
            lineHeight: 1.7,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {excerpt}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Avatar src={post.author.avatar} alt={post.author.name} sx={{ width: 40, height: 40 }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {post.author.name}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formattedDate}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {readTime}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>

        <ReadMoreButton
          component={Link}
          to={`/blog/${post.slug}`}
          endIcon={<ArrowForwardIcon />}
          className="read-more-button"
          fullWidth
        >
          {language === 'tr' ? 'Devamını Oku' : 'Read More'}
        </ReadMoreButton>
      </CardContent>
    </StyledCard>
  );
}